/* Fluid main-PDP photo geometry. Existing media nodes and lightbox hooks stay in place.
   No image requests, polling, third-party library, cloned gallery or scroll resizing. */
(function () {
  'use strict';
  if (window.__mipFluidGalleryInstalled) return;
  window.__mipFluidGalleryInstalled = true;
  var selector = '.mip-has-tiled-gallery [data-mip-fluid-gallery]';
  var instances = new Map();

  function create(grid) {
    var section = grid.closest('.product-details');
    var desktop = matchMedia('(min-width: 768px)');
    var frame = 0, stopped = false, lastWidth = -1;
    var backups = new Map();
    var sizeObserver, structureObserver;
    var initialTop = null;

    function schedule() {
      if (stopped || frame) return;
      frame = requestAnimationFrame(function () { frame = 0; layout(); });
    }
    function photo(tile) { return tile.querySelector('img[data-main-image]'); }
    function remember(image) { if (!backups.has(image)) backups.set(image, image.getAttribute('sizes')); }
    function restore() {
      if (grid.classList.contains('mip-fluid-ready')) grid.classList.remove('mip-fluid-ready');
      grid.style.removeProperty('--mip-hero-height');
      grid.querySelectorAll('.productView-image').forEach(function (tile) {
        tile.style.removeProperty('--mip-tile-span');
        tile.style.removeProperty('--mip-tile-start');
        tile.style.removeProperty('--mip-tile-column');
        tile.classList.remove('mip-gallery-hero');
      });
      backups.forEach(function (sizes, image) {
        if (sizes === null) image.removeAttribute('sizes'); else image.setAttribute('sizes', sizes);
      });
    }
    function layout() {
      if (!grid.isConnected) { destroy(); return; }
      if (!desktop.matches || grid.classList.contains('slick-initialized')) { restore(); return; }
      var bounds = grid.getBoundingClientRect();
      if (bounds.width <= 0) return;
      // The document coordinate is stable while scrolling: do NOT resize the hero
      // to the changing visible slice of the gallery as a shopper scrolls down.
      var documentTop = bounds.top + window.scrollY;
      if (initialTop === null || window.scrollY < 20) initialTop = documentTop;
      var viewportHeight = document.documentElement.clientHeight || window.innerHeight;
      var cap = Math.max(80, Math.floor(viewportHeight - initialTop - 12));
      var heightValue = cap + 'px';
      if (grid.style.getPropertyValue('--mip-hero-height') !== heightValue) grid.style.setProperty('--mip-hero-height', heightValue);
      var tiles = Array.from(grid.children).filter(function (tile) {
        return tile.classList.contains('productView-image') && getComputedStyle(tile).display !== 'none';
      });
      if (!tiles.length) return;
      grid.querySelectorAll('.mip-gallery-hero').forEach(function (tile) { if (tile !== tiles[0]) tile.classList.remove('mip-gallery-hero'); });
      tiles[0].classList.add('mip-gallery-hero');
      var gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
      // One read batch, then a write batch. Row spans use intrinsic photo ratios;
      // no image is cropped or made to match a neighbour's height.
      var sizes = tiles.map(function (tile, index) {
        var image = photo(tile);
        var rect = tile.getBoundingClientRect();
        var width = index === 0 ? bounds.width : (bounds.width - gap) / 2;
        var ratio = Number(tile.dataset.mipPhotoRatio) || (image && image.naturalWidth / image.naturalHeight) || 1;
        var height = image ? Math.min(width / ratio, index === 0 ? cap : Infinity) : rect.height;
        return { tile: tile, image: image, rows: Math.max(1, Math.ceil(height + gap)), pixels: index === 0 ? Math.min(width, cap * ratio) : width };
      });
      // Two continuous stacks, alternated in the original media order. Each
      // column advances by its own image height rather than its neighbour's.
      var nextRow = [1, 1];
      sizes.forEach(function (item, index) {
        var column = index === 0 ? 0 : (index - 1) % 2;
        var start = index === 0 ? 1 : nextRow[column];
        item.tile.style.setProperty('--mip-tile-start', String(start));
        item.tile.style.setProperty('--mip-tile-column', String(column + 1));
        if (index === 0) nextRow = [1 + item.rows, 1 + item.rows];
        else nextRow[column] += item.rows;
        var rows = String(item.rows);
        if (item.tile.style.getPropertyValue('--mip-tile-span') !== rows) item.tile.style.setProperty('--mip-tile-span', rows);
        if (item.image) {
          remember(item.image);
          var pixels = Math.ceil(item.pixels) + 'px';
          if (item.image.getAttribute('sizes') !== pixels) item.image.setAttribute('sizes', pixels);
        }
      });
      if (!grid.classList.contains('mip-fluid-ready')) grid.classList.add('mip-fluid-ready');
    }
    function resize() { initialTop = null; schedule(); }
    function loaded(event) { if (event.target.matches('img, video, model-viewer')) schedule(); }
    function destroy() {
      if (stopped) return;
      stopped = true; cancelAnimationFrame(frame);
      if (sizeObserver) sizeObserver.disconnect();
      if (structureObserver) structureObserver.disconnect();
      desktop.removeEventListener('change', resize);
      window.removeEventListener('resize', resize);
      grid.removeEventListener('load', loaded, true);
      grid.removeEventListener('loadedmetadata', loaded, true);
      restore(); instances.delete(grid);
    }
    desktop.addEventListener('change', resize);
    window.addEventListener('resize', resize, { passive: true });
    grid.addEventListener('load', loaded, true);
    grid.addEventListener('loadedmetadata', loaded, true);
    if ('ResizeObserver' in window) {
      sizeObserver = new ResizeObserver(function (entries) {
        // Height changes caused by our own row packing are deliberately ignored.
        var width = entries[0].contentRect.width;
        if (Math.abs(width - lastWidth) > 0.5) { lastWidth = width; schedule(); }
      });
      sizeObserver.observe(grid);
    }
    structureObserver = new MutationObserver(schedule);
    structureObserver.observe(grid, { childList: true, attributes: true, attributeFilter: ['class'] });
    // First measurement uses the current layout. Re-measure after fonts/load to
    // account for header reflow, without attaching a scroll listener.
    schedule();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { if (!stopped) schedule(); });
    return { destroy: destroy, schedule: schedule, section: section };
  }
  function init(scope) {
    (scope || document).querySelectorAll(selector).forEach(function (grid) {
      if (!instances.has(grid)) instances.set(grid, create(grid));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); }, { once: true });
  else init();
  window.addEventListener('load', function () { instances.forEach(function (instance) { instance.schedule(); }); }, { once: true });
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    instances.forEach(function (instance, grid) { if (event.target.contains(grid)) instance.destroy(); });
  });
})();
